import type { BaseAPIParams } from 'lib/utils/BaseAPIParams';
import { BaseApiClient } from '../baseApiClient';

export type ReplayMessagesArg =
  | {
      dateFrom: string | undefined;
      dateTo: string | undefined;
      dataSourceId: string;
    }
  | { requestLogId: string };

export type PostDecoderArg = unknown;

export type DecoderFilter = BaseAPIParams<unknown> & {
  dateFrom?: Date | string;
  dateTo?: Date | string;
  /** literal, case-insensitive text in the message URL or body */
  search?: string;
  customerId?: string;
  /** comma-separated data source prefixes */
  dataSourceIds?: string;
  status?: IngestStatus;
  /** endpoint route pattern, as listed in the stats */
  route?: string;
  /** only messages received before this time, the cursor for loading older ones */
  before?: string;
};

export type IngestStatus = 'pending' | 'processed' | 'failed';

export type DecoderLog = {
  /** request log id, missing for messages decoded from the Plain tab */
  id?: string;
  url?: string;
  route?: string;
  body: unknown;
  createdAt?: string;
  customerId?: string;
  dataSourceIds?: string[];
  source?: 'http' | 'mqtt';
  /** how the service handled the message when it arrived, independent of decoding it again now */
  ingestStatus?: IngestStatus;
  ingestError?: string;
  processedAt?: string;
  replayed?: boolean;
};

export const STATS_WINDOWS_MINUTES = [15, 60, 360] as const;
export type StatsWindowMinutes = (typeof STATS_WINDOWS_MINUTES)[number];

export type DecoderStatsFilter = {
  windowMinutes: StatsWindowMinutes;
  customerId?: string;
  dataSourceIds?: string;
};

type Percentiles = { p50: number | null; p95: number | null; p99: number | null };

export type DecoderRouteStats = Percentiles & {
  route: string;
  count: number;
  failed: number;
  pending: number;
  lastAt: string;
};

export type DataSourceHealth = {
  dataSourceId: string;
  lastSeenAt: string | null;
  count24h: number;
  failed24h: number;
  medianIntervalSeconds: number | null;
  maxGapSeconds: number | null;
  maxGapEndedAt: string | null;
};

export type DecoderStats = Percentiles & {
  windowMinutes: StatsWindowMinutes;
  bucketMinutes: number;
  from: string;
  to: string;
  total: number;
  failed: number;
  pending: number;
  buckets: { at: string; count: number; failed: number }[];
  routes: DecoderRouteStats[];
  dataSource?: DataSourceHealth;
};

export type DecoderLogEntry =
  | { status: 'ok'; log: DecoderLog; result: unknown[] }
  | { status: 'error'; log: DecoderLog; error: string };

export class DecoderApiClient extends BaseApiClient {
  constructor(jwt: string) {
    super('enmon-tools/decoder', undefined, jwt);
  }

  public async get(filter: DecoderFilter, signal: AbortSignal | undefined): Promise<DecoderLogEntry[]> {
    const response = await this.axios.get<DecoderLogEntry[]>(``, {
      params: filter,
      signal,
    });

    return response.data;
  }

  public async getStats(filter: DecoderStatsFilter, signal: AbortSignal | undefined): Promise<DecoderStats> {
    const response = await this.axios.get<DecoderStats>('stats', { params: filter, signal });

    return response.data;
  }

  public async post(messages: PostDecoderArg, customerId: string, signal: AbortSignal) {
    const response = await this.axios.post('', messages, {
      signal,
      params: {
        customerId,
      },
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  }

  public async replayMessages(payload: ReplayMessagesArg): Promise<void> {
    await this.axios.post(`/replay-messages`, payload);
  }

  public async mbusToJson(payload: string): Promise<void> {
    const response = await this.axios.post('mbus-to-json', { payload });

    return response.data;
  }
}
